import { Component } from '@theme/component';
import { RecentlyViewed } from '@theme/recently-viewed-products';

/**
 * Recently-viewed product carousel (see sections/ts-recently-viewed-carousel.liquid).
 *
 * Recently-viewed product ids live in localStorage, written on product pages via
 * snippets/scripts.liquid. This component reads them, fetches the matching cards
 * server-rendered through the shared ts-carousel-product-card pipeline via the
 * Search section-rendering endpoint, and mounts the carousel shell from the inert
 * <template>. With no stored products the section stays hidden.
 *
 * @typedef {object} Refs
 * @property {HTMLTemplateElement} [carouselTemplate]
 *
 * @extends {Component<Refs>}
 */
class RecentlyViewedCarousel extends Component {
  /** @type {AbortController | null} */
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();
    this.#build();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#activeFetch?.abort();
  }

  /** Fetches the stored products' cards and reveals the carousel. */
  async #build() {
    const productIds = RecentlyViewed.getProducts();
    if (productIds.length === 0) return;

    try {
      const { slides, stylesheet } = await this.#fetchSlides(productIds);
      if (!slides.length) return;

      this.#mount(slides, stylesheet);
      this.hidden = false;
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      console.error('Recently viewed carousel error:', error);
    }
  }

  /**
   * Re-renders this section in search context to get the product slides. The id
   * query preserves order so the most-recently viewed product stays first.
   *
   * @param {string[]} productIds
   * @returns {Promise<{ slides: Element[], stylesheet: Element | null }>}
   */
  async #fetchSlides(productIds) {
    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    const sectionId = this.dataset.sectionId || 'ts-recently-viewed-carousel';
    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', productIds.map((/** @type {string} */ id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('section_id', sectionId);

    const response = await fetch(url, { signal: this.#activeFetch.signal });
    this.#activeFetch = null;
    if (!response.ok) return { slides: [], stylesheet: null };

    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const stylesheet = doc.querySelector('style[data-section-stylesheet]');
    const slides = Array.from(doc.querySelectorAll('.ts-carousel-product-card'));

    return { slides, stylesheet };
  }

  /**
   * Populates the shell from the inert template and swaps it in.
   *
   * @param {Element[]} slides
   * @param {Element | null} stylesheet
   */
  #mount(slides, stylesheet) {
    const template = this.refs.carouselTemplate;
    if (!(template instanceof HTMLTemplateElement)) return;

    const content = /** @type {DocumentFragment} */ (template.content.cloneNode(true));
    const scroller = content.querySelector('slideshow-slides');
    if (!scroller) return;

    for (const slide of slides) scroller.append(slide);

    // The shell renders with slide_count 0, which disables the next arrow.
    if (slides.length > 1) content.querySelector('[ref="next"]')?.removeAttribute('disabled');

    // Stylesheet tags are page-scoped; the SFR response carries the card CSS.
    if (stylesheet && !document.querySelector('style[data-ts-recently-viewed-card-styles]')) {
      const styleClone = /** @type {HTMLStyleElement} */ (stylesheet.cloneNode(true));
      styleClone.setAttribute('data-ts-recently-viewed-card-styles', '');
      this.prepend(styleClone);
    }

    template.replaceWith(content);
  }
}

if (!customElements.get('ts-recently-viewed')) {
  customElements.define('ts-recently-viewed', RecentlyViewedCarousel);
}
